"use client"

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

/** Layout profiles for common display aspect ratios (incl. ~2.35:1 / 2.39:1 ultrawide). */
export type DisplayAspectId = "auto" | "4-3" | "16-9" | "21-9"

export interface DisplayAspectOption {
  id: DisplayAspectId
  ratioLabel: string
}

export const DISPLAY_ASPECTS: DisplayAspectOption[] = [
  { id: "auto", ratioLabel: "Auto" },
  { id: "4-3", ratioLabel: "4∶3" },
  { id: "16-9", ratioLabel: "16∶9" },
  { id: "21-9", ratioLabel: "21∶9" },
]

const STORAGE_KEY = "pos-display-aspect"

interface DisplayAspectContextType {
  aspect: DisplayAspectId
  setAspect: (id: DisplayAspectId) => void
}

const DisplayAspectContext = createContext<DisplayAspectContextType | undefined>(undefined)

function applyDisplayAspect(id: DisplayAspectId) {
  document.documentElement.dataset.displayAspect = id
}

export function DisplayAspectProvider({ children }: { children: ReactNode }) {
  const [aspect, setAspectState] = useState<DisplayAspectId>("auto")

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as DisplayAspectId | null
    const valid = DISPLAY_ASPECTS.some((o) => o.id === saved)
    const initial = valid && saved ? saved : "auto"
    setAspectState(initial)
    applyDisplayAspect(initial)
  }, [])

  const setAspect = useCallback((id: DisplayAspectId) => {
    setAspectState(id)
    localStorage.setItem(STORAGE_KEY, id)
    applyDisplayAspect(id)
  }, [])

  return (
    <DisplayAspectContext.Provider value={{ aspect, setAspect }}>
      {children}
    </DisplayAspectContext.Provider>
  )
}

export function useDisplayAspect() {
  const ctx = useContext(DisplayAspectContext)
  if (!ctx) throw new Error("useDisplayAspect must be used within DisplayAspectProvider")
  return ctx
}

export function displayAspectLabelKey(id: DisplayAspectId): "displayAspectAuto" | "displayAspect43" | "displayAspect169" | "displayAspect219" {
  if (id === "4-3") return "displayAspect43"
  if (id === "16-9") return "displayAspect169"
  if (id === "21-9") return "displayAspect219"
  return "displayAspectAuto"
}

export function displayAspectDescKey(id: DisplayAspectId): "displayAspectAutoDesc" | "displayAspect43Desc" | "displayAspect169Desc" | "displayAspect219Desc" {
  if (id === "4-3") return "displayAspect43Desc"
  if (id === "16-9") return "displayAspect169Desc"
  if (id === "21-9") return "displayAspect219Desc"
  return "displayAspectAutoDesc"
}
